/**
 * Quotation Summary Contract
 * 
 * Single source of truth for Live Preview.
 * All calculations happen here, preview is purely presentational.
 */

/**
 * Calculate quotation summary from quotation data
 * @param {Object} quotation - Raw quotation data
 * @param {Object} companySettings - Company settings for state comparison
 * @returns {Object} quotationSummary - Derived summary for preview
 */
export function calculateQuotationSummary(quotation, companySettings = null) {
  const items = quotation.items || [];

  // Calculate subtotal
  const subtotal = items.reduce((sum, item) => {
    const base = (item.quantity || 0) * (item.rate || 0);
    const discount =
      item.discountType === "percentage"
        ? (base * (item.discount || 0)) / 100
        : item.discount || 0;
    return sum + Math.max(0, base - discount);
  }, 0);

  // Calculate tax
  const companyState = companySettings?.state || "Maharashtra";
  const clientState = quotation.client?.state || "Maharashtra";
  
  // Normalize state names for comparison (case-insensitive, trim whitespace)
  const normalizeState = (state) => state?.toLowerCase().trim() || "";
  const sameState = normalizeState(companyState) === normalizeState(clientState);
  
  console.log('Quotation tax calculation:', {
    companyState,
    clientState,
    sameState,
    normalizedCompany: normalizeState(companyState),
    normalizedClient: normalizeState(clientState)
  });

  let cgst = 0, sgst = 0, igst = 0;

  items.forEach((item) => {
    const amount = Math.max(
      0,
      (item.quantity || 0) * (item.rate || 0) -
        ((item.discountType === "percentage"
          ? ((item.quantity || 0) * (item.rate || 0) * (item.discount || 0)) / 100
          : item.discount) || 0)
    );

    const tax = (amount * (item.gstRate || item.tax || 0)) / 100;

    if (sameState) {
      cgst += tax / 2;
      sgst += tax / 2;
    } else {
      igst += tax;
    }
  });

  const taxTotal = cgst + sgst + igst;

  // Calculate adjustments
  const discountType = quotation.discountType || "flat";
  const discountValue = quotation.discount || 0;
  const discountAmount =
    discountType === "percentage"
      ? (subtotal * discountValue) / 100
      : discountValue;

  const shipping = quotation.shipping || 0;
  const rounding = quotation.roundOff || 0;
  const adjustments = -discountAmount + shipping + rounding;

  // Calculate totals
  const total = subtotal + taxTotal + adjustments;

  // Extract location from billing address
  const extractLocation = (address) => {
    if (!address) return null;
    const lines = address.split('\n').filter(line => line.trim());
    const lastLine = lines[lines.length - 1];
    if (!lastLine) return null;
    const match = lastLine.match(/^([^,]+),\s*([A-Za-z\s]+)/);
    if (match) return `${match[1]}, ${match[2]}`;
    return lastLine;
  };

  // Build summary contract
  return {
    // Quotation meta
    quoteNumber: quotation.quoteNumber || "QUO-0001",
    quoteDate: quotation.quoteDate,
    validUntil: quotation.validUntil,
    status: quotation.status || "draft",
    
    // Bill to
    billTo: quotation.client ? {
      name: quotation.client.name,
      location: extractLocation(quotation.billingAddress),
    } : null,
    
    // Items (simplified for preview)
    items: items.map(item => {
      const qty = Number(item.quantity) || 0;
      const rate = Number(item.rate) || 0;
      const base = qty * rate;
      const discount =
        item.discountType === "percentage"
          ? (base * (Number(item.discount) || 0)) / 100
          : Number(item.discount) || 0;
      const amount = Math.max(0, base - discount);

      // Metadata
      const metadata = [];
      if (item.type === "product" && item.hsn) metadata.push(`HSN ${item.hsn}`);
      if (item.type === "service" && item.sac) metadata.push(`SAC ${item.sac}`);
      if (item.uom) metadata.push(item.uom);

      return {
        name: item.name || "Untitled Item",
        quantity: qty,
        rate: rate,
        amount: amount,
        metadata: metadata.join(" • ") || null,
      };
    }),
    
    // Totals
    subtotal,
    taxTotal,
    adjustments: adjustments !== 0 ? adjustments : null,
    total,
    
    // Tax breakdown (for full quotation view)
    taxBreakdown: {
      cgst,
      sgst,
      igst,
      sameState,
    },
    
    // Flags
    hasItems: items.length > 0,
    hasClient: !!quotation.client,
    hasAdjustments: adjustments !== 0,
  };
}

/**
 * Validate quotation summary contract
 * Ensures all required fields are present
 */
export function validateQuotationSummary(summary) {
  const required = [
    'quoteNumber',
    'status',
    'items',
    'subtotal',
    'taxTotal',
    'total',
  ];

  const missing = required.filter(field => summary[field] === undefined);
  
  if (missing.length > 0) {
    console.error('Invalid quotation summary contract. Missing fields:', missing);
    return false;
  }

  return true;
}
